 
/**
 * Authentication service for managing user login/logout with Django backend
 */

import Cookies from 'js-cookie';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  message?: string;
  status?: number;
}

/**
 * Authenticates user with Django backend
 */
export const login = async (credentials: LoginCredentials): Promise<AuthResponse> => {
  try {
    const csrfToken = Cookies.get('csrftoken') || '';
    
    const response = await fetch('/api/login/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken,
      },
      credentials: 'include', // Include cookies for CSRF and session
      body: JSON.stringify({
        email: credentials.email.toLowerCase().trim(),
        password: credentials.password,
      }),
    });

    if (response.ok) {
      return { success: true };
    }

    // Handle specific error cases
    switch (response.status) {
      case 400:
        return { 
          success: false, 
          message: 'Please provide both email and password',
          status: 400 
        };
      case 401:
        return { 
          success: false, 
          message: 'Invalid email or password',
          status: 401 
        };
      case 403: {
        const errorData = await response.json();
        if (errorData.message === 'login.emailNotVerified') {
          return { 
            success: false, 
            message: 'Please verify your email address before logging in',
            status: 403 
          };
        }
        return { 
          success: false, 
          message: 'Access denied',
          status: 403 
        };
      }
      default:
        return { 
          success: false, 
          message: 'An unexpected error occurred. Please try again.',
          status: response.status 
        };
    }
  } catch {
    return { 
      success: false, 
      message: 'Network error. Please check your connection and try again.' 
    };
  }
};

/**
 * Logs out the current user
 */
export const logout = async (): Promise<AuthResponse> => {
  try {
    // First, make a GET request to ensure we have a fresh CSRF token
    await fetch('/api/', { 
      method: 'GET',
      credentials: 'include'
    });
    
    // Get the CSRF token from cookies after the GET request
    const csrfToken = Cookies.get('csrftoken');
    
    if (!csrfToken) {
      return { 
        success: false, 
        message: 'Authentication error: CSRF token missing' 
      };
    }
    
    // Use the fresh CSRF token for logout
    const response = await fetch('/api/logout/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken,
      },
      credentials: 'include',
      // Add empty body to ensure proper POST request
      body: JSON.stringify({}),
    });

    if (response.ok) {
      return { success: true };
    }
    
    // Handle server errors (both 500 and 403)
    if (response.status === 500 || response.status === 403) {
      // For these errors, we'll consider it a successful logout on the frontend side
      // since we'll clean up the session on the client regardless
      
      // Try an alternative logout approach for 403 errors
      if (response.status === 403) {
        // Try to access the Django built-in logout URL as a fallback
        try {
          // Make a GET request to Django's built-in logout URL
          fetch('/auth/logout/', { 
            method: 'GET',
            credentials: 'include'
          });
        } catch {
          // Alternative logout approach ignored  
        }
      }
      
      return { success: true };
    }

    return { 
      success: false, 
      message: `Failed to logout properly (Status: ${response.status})`,
      status: response.status 
    };
  } catch {
    return { 
      success: false, 
      message: 'Network error during logout' 
    };
  }
};